import { useCallback, useMemo } from 'react';
import { FormOrderBy } from '@weave/schema-gen-ts/dist/schemas/forms-digital/forms_messages.pb';
import { DigitalFormsQueries } from '@frontend/api-digital-forms';
import { useDigitalFormsFreemiumContext } from '@frontend/digital-forms-scope';
import { useAppScopeStore } from '@frontend/scope';
import { usePreference } from '@frontend/user-preferences';
import { useDebouncedValue, useFormField, useSwitchChipGroup } from '@frontend/design-system';
import { Category } from '../types/form-packet-selector.types';

type UseFormPacketListArgs = {
  groupId: string;
  singleSelect?: boolean;
};

export const useFormPacketList = ({ groupId, singleSelect = true }: UseFormPacketListArgs) => {
  const searchFieldProps = useFormField({
    type: 'text',
  });
  const [storedSortOrder] = usePreference('FormsLibrarySortOrder');
  const debouncedSearchValue = useDebouncedValue(searchFieldProps.value, 300);
  const switchGroup = useSwitchChipGroup();
  const { value: switchGroupValue, setValue: switchGroupSetValue } = switchGroup;
  const {
    isActionBlocked,
    featureUpgradeDetailsModalControls: { openModal },
  } = useDigitalFormsFreemiumContext();
  const { selectedOrgId } = useAppScopeStore();
  const orderBy = storedSortOrder ?? FormOrderBy.FORM_ORDER_CREATED_AT;
  const locationIds = useMemo<string[]>(() => [groupId], [groupId]);

  // Wrap setValue to intercept packet selection and check freemium access
  const handleSetValue = useCallback(
    (newValue: string[] | ((prev: string[]) => string[])) => {
      const resolvedValue = typeof newValue === 'function' ? newValue(switchGroupValue) : newValue;

      // Check if user is trying to switch to packets and if they're blocked
      if (resolvedValue.includes(Category.PACKETS) && isActionBlocked('packet_creation')) {
        openModal();
        return; // Don't change the value
      }

      switchGroupSetValue(newValue);
    },
    [switchGroupValue, switchGroupSetValue, isActionBlocked, openModal]
  );

  const formsQuery = DigitalFormsQueries.useFormListV3(
    {
      orgRequest: {
        orgId: selectedOrgId,
        businessGroupIds: locationIds,
      },
      searchKey: debouncedSearchValue,
      pageRequest: {
        pageLimit: 20,
        pageNumber: 1,
      },
      orderBy: orderBy,
    },
    {
      enabled: switchGroupValue.includes(Category.FORMS),
    }
  );

  const packetsQuery = DigitalFormsQueries.usePacketListV3(
    {
      orgRequest: {
        orgId: selectedOrgId,
        businessGroupIds: locationIds,
      },
      searchKey: debouncedSearchValue,
      pageRequest: {
        pageLimit: 20,
        pageNumber: 1,
      },
      orderBy: DigitalFormsQueries.FORM_ORDER_TO_PACKET_ORDER[orderBy],
    },
    {
      enabled: switchGroupValue.includes(Category.PACKETS),
    }
  );

  return {
    searchFieldProps: {
      ...searchFieldProps,
      name: 'form-packet-search',
    },
    switchChipGroupProps: {
      ...switchGroup,
      setValue: handleSetValue,
      singleSelect,
    },
    formsQuery,
    packetsQuery,
  };
};
